import { VmButton } from 'Components/vm-button/vm-button';
import { customElement } from 'lit/decorators.js';

/**
 * A vm-button for native form navigations. Defaults to type="submit" and keeps
 * all vm-button styling, slots, and accessibility features.
 *
 * Observes the containing form, so clicks, Enter, and requestSubmit() share one
 * submission guard. Submissions canceled by form validation do not start loading.
 * Back/Forward restores and Escape clear loading; call reset() to allow a retry
 * when navigation is canceled in another way without leaving the page.
 *
 * @example
 * ```html
 * <form action="/search/" method="get">
 *   <input name="q" required>
 *   <vm-submit-button>Search</vm-submit-button>
 * </form>
 * ```
 */
@customElement('vm-submit-button')
export class VmSubmitButton extends VmButton {
    override type: VmButton['type'] = 'submit';

    private _form: HTMLFormElement | null = null;
    private _submitRoot: Node | null = null;

    override connectedCallback(): void {
        super.connectedCallback();
        this._form = this.closest('form');
        if (!this._form) return;

        this._submitRoot = this._form.getRootNode();
        this._form.addEventListener('submit', this._guardSubmission, { capture: true });
        this._submitRoot.addEventListener('submit', this._handleSubmit);
        window.addEventListener('pageshow', this.reset);
        window.addEventListener('keydown', this._handleKeyDown);
    }

    override disconnectedCallback(): void {
        this._form?.removeEventListener('submit', this._guardSubmission, { capture: true });
        this._submitRoot?.removeEventListener('submit', this._handleSubmit);
        window.removeEventListener('pageshow', this.reset);
        window.removeEventListener('keydown', this._handleKeyDown);
        this._form = null;
        this._submitRoot = null;
        this.reset();
        super.disconnectedCallback();
    }

    /** Clears loading and allows another submission. */
    reset = (): void => {
        this.loading = false;
    };

    private _handleKeyDown = (event: KeyboardEvent): void => {
        // Escape can stop a pending navigation without firing pageshow.
        if (event.key === 'Escape' && !event.defaultPrevented && this.loading) {
            this.reset();
        }
    };

    private _guardSubmission = (event: Event): void => {
        if (event.target !== this._form || !this.loading) return;

        event.preventDefault();
        // Stop repeat submissions before form analytics and other handlers run.
        event.stopImmediatePropagation();
    };

    private _handleSubmit = (event: Event): void => {
        // Bubble to the form's root so form handlers can cancel validation first.
        if (event.target === this._form && !event.defaultPrevented) {
            this.loading = true;
        }
    };
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-submit-button': VmSubmitButton;
    }
}
